<template>
    <nav class="guideline-navigation">
        <nuxt-link
            v-for="subItem in subItems"
            v-show="showSubItem(subItem)"
            :key="subItem.path"
            ref="navItem"
            class="guideline-navigation__item"
            :to="`/guidelines/${guidelineSlug}${subItem.path}`"
            :data-active="isItemActive(subItem) ? 'true' : null"
        >
            {{ subItem.title }}
        </nuxt-link>
        <span
            ref="underline"
            class="guideline-navigation__underline"
        />
    </nav>
</template>

<script setup>
const { featureFlag } = useMultisite();
const route = useRoute();
const props = defineProps({
    guidelineSlug: {
        type: String,
        required: false,
        default: ''
    },
    showRelatedContent: {
        type: Boolean,
        required: false,
        default: false
    }
});

const subItems = computed(() => {
    const items = [
        { title: 'Full text guideline', path: '' },
        { title: 'Summary of Changes', path: '/summary-of-changes' },
        { title: 'Panel', path: '/panel' },
        { title: 'Related content', path: '/related-content' },
    ];

    if (featureFlag('guideline-translations')) {
        items.push({ title: 'Translations', path: '/translations' });
    }
    if (featureFlag('summaries-not-publications-and-appendices')) {
        items.splice(2, 0, ({ title: 'Summaries', path: '/summaries' }));
    } else {
        items.splice(2, 0,({ title: 'Publications & Appendices', path: '/publications-appendices' }));
    }

    return items;
});

watch(() => route.path, () => {
    moveUnderline(route);
}, { immediate: true });

function isItemActive(item) {
    const prefix = `/guidelines/${props.guidelineSlug}`;
    switch (item.path) {
    case '':
        return route.path === prefix || route.path.startsWith(prefix + '/chapter') || route.path === prefix + '/';
    default:
        return route.path.startsWith(prefix + item.path);
    }
}

function showSubItem(item) {
    return !(item.title === 'Related content' && !props.showRelatedContent);

}

function onResize() {
    moveUnderline(route);
}

const underline = ref(null);
const navItem = ref(null);

function moveUnderline() {
    nextTick(() => {
        // Strip prefix
        const match = navItem?.value?.filter((ref) => {
            return ref.$el?.hasAttribute?.('data-active');
        })?.[0];

        if (match && match.$el) {
            underline.value.style.left = `${match.$el.offsetLeft}px`;
            underline.value.style.width = `${match.$el.clientWidth}px`;
        }
    });
}

onMounted(() => {
    if(typeof window === 'undefined') {
        return;
    }
    moveUnderline(route);
    window.addEventListener('resize', onResize);
});

onUnmounted(() => {
    if(typeof window === 'undefined') {
        return;
    }
    window.removeEventListener('resize', onResize);
});
</script>

<style lang="less">
.guideline-navigation {
    position: relative;
    display: flex;
    margin: 2rem 0 0;

    flex-direction: column;
    align-items: flex-start;

    @media @q-md-min {
        overflow-x: auto;
        flex-direction: row;
        padding-bottom: .375rem;
    }

    @media @q-lg-min {
        margin: 2.5rem 0 .5rem;
    }
}

.guideline-navigation__item {
    .typography-h4;

    border-bottom: 2px solid transparent;

    text-decoration: none;
    transition: color var(--transition-fast), border-color var(--transition-fast);
    white-space: nowrap;

    &:hover, &:focus,
    &[data-active='true'] {
        color: var(--color-primary);
        border-bottom-color: var(--color-primary);
    }

    &:active {
        color: var(--color-primary-dark);
    }

    & + .guideline-navigation__item {
        margin-top: 1rem;
    }

    @media @q-md-min {
        border-bottom: 0;

        & + .guideline-navigation__item {
            margin-top: 0;
            margin-left: 1rem;
        }
    }

    @media @q-lg-min {
        & + .guideline-navigation__item {
            margin-left: 2.5rem;
        }
    }
}

.guideline-navigation__underline {
    display: none;

    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    background-color: var(--color-primary);

    transition: left var(--transition-fast), width var(--transition-fast);

    @media @q-md-min {
        display: block;
    }
}
</style>
